import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  Building2, Tag, ShoppingBag, ListChecks, Megaphone, Wine, Package,
  TrendingUp, Clock, CheckCircle2, XCircle, AlertCircle, ArrowRight, ShieldCheck,
} from "lucide-react";

export default function B2BDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [myLots, setMyLots] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [marketplaceCount, setMarketplaceCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, m, mk] = await Promise.all([
          api.get("/b2b/profile").catch(() => null),
          api.get("/b2b/lots/mine").catch(() => null),
          api.get("/b2b/lots/marketplace").catch(() => null),
        ]);
        if (p?.data) setProfile(p.data);
        if (m?.data) {
          setMyLots(m.data.lots || []);
          setByStatus(m.data.by_status || {});
        }
        if (mk?.data) setMarketplaceCount(mk.data.total || 0);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const totalRevenue = myLots
    .filter((l) => l.status === "approved" || l.status === "sold")
    .reduce((sum, l) => sum + (l.total_price || 0), 0);

  // Anagrafica completion estimate
  const profileCompletion = (() => {
    if (!profile?.profile) return 0;
    const p = profile.profile;
    const fields = ["company_name", "vat_number", "address", "city", "contact_phone", "iban", "haccp_license"];
    const filled = fields.filter((f) => p[f] && String(p[f]).trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  })();

  return (
    <div data-testid="b2b-dashboard-page" className="container-xl py-8 lg:py-10">
      {/* HEADER */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold mb-2">Backend rivenditore</div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="editorial-heading text-4xl md:text-5xl">
            Bentornato, <span className="italic text-[hsl(var(--accent))]">{user?.name?.split(" ")[0] || "rivenditore"}</span>
          </h1>
          {profile?.verified && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> Account verificato
            </div>
          )}
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="b2b-kpi-row">
        <KpiCard icon={Tag} label="Miei lotti" value={myLots.length} testid="kpi-my-lots" tone="primary" />
        <KpiCard icon={Clock} label="In attesa" value={byStatus.pending_approval || 0} testid="kpi-pending" tone="amber" />
        <KpiCard icon={CheckCircle2} label="Approvati" value={byStatus.approved || 0} testid="kpi-approved" tone="emerald" />
        <KpiCard icon={ShoppingBag} label="Lotti marketplace" value={marketplaceCount} testid="kpi-marketplace" tone="accent" />
        <KpiCard icon={TrendingUp} label="Valore attivo" value={`€ ${totalRevenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} testid="kpi-revenue" tone="primary" />
      </div>

      {/* PROFILE COMPLETION ALERT */}
      {profileCompletion < 70 && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-sm p-5 flex items-start gap-3" data-testid="profile-incomplete">
          <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Anagrafica incompleta ({profileCompletion}%)</div>
            <div className="text-sm text-amber-800 mt-0.5">
              Completa P.IVA, indirizzo, IBAN e licenze HACCP per pubblicare lotti senza limiti e ricevere bonifici diretti.
            </div>
          </div>
          <Link to="/b2b/anagrafica" className="px-4 py-2 bg-amber-700 text-white text-xs font-semibold rounded-sm hover:bg-amber-800 shrink-0">
            Completa ora →
          </Link>
        </div>
      )}

      {/* QUICK ACTIONS */}
      <h2 className="editorial-heading text-2xl mb-4">Strumenti rivenditore</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="b2b-tools-grid">
        <ToolCard to="/b2b/anagrafica" icon={Building2} title="Anagrafica" desc="Profilo · IBAN · licenze · zone" testid="dash-tool-anagrafica" />
        <ToolCard to="/b2b/lots/sell" icon={Tag} title="Vendi lotto" desc="Pubblica vini o food box" testid="dash-tool-sell" highlight />
        <ToolCard to="/b2b/lots/marketplace" icon={ShoppingBag} title="Acquista lotti" desc="Marketplace lotti" testid="dash-tool-buy" />
        <ToolCard to="/b2b/lots/mine" icon={ListChecks} title="I miei lotti" desc="Stato pubblicazioni" testid="dash-tool-mine" />
        <ToolCard to="/b2b/marketing" icon={Megaphone} title="Marketing" desc="Crea campagna" testid="dash-tool-marketing" />
      </div>

      {/* RECENT LOTS */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4" /> Ultimi lotti pubblicati</h3>
            <Link to="/b2b/lots/mine" className="text-xs text-[hsl(var(--accent))] hover:underline">Vedi tutti →</Link>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Caricamento…</div>
          ) : myLots.length === 0 ? (
            <div className="p-6 text-center" data-testid="empty-lots">
              <Package className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <div className="text-sm text-muted-foreground mb-3">Non hai ancora pubblicato lotti.</div>
              <Link to="/b2b/lots/sell" className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white text-sm font-semibold rounded-sm">
                <Tag className="h-4 w-4" /> Pubblica il primo lotto
              </Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2"></th>
                  <th className="text-left px-4 py-2">Titolo</th>
                  <th className="text-right px-4 py-2">Qty</th>
                  <th className="text-right px-4 py-2">Valore</th>
                  <th className="text-center px-4 py-2">Stato</th>
                </tr>
              </thead>
              <tbody>
                {myLots.slice(0, 5).map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="px-4 py-2.5">{l.product_type === "wine" ? <Wine className="h-4 w-4 text-[#7A1C2B]" /> : <Package className="h-4 w-4 text-[#C9A961]" />}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium truncate max-w-[260px]">{l.title}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(l.created_at).toLocaleDateString("it-IT")}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right">{l.quantity}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">€ {l.total_price?.toFixed(0)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <StatusPill status={l.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* MARKETPLACE TEASER */}
        <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-[#5A0F1F] text-white rounded-sm p-6 flex flex-col">
          <ShoppingBag className="h-8 w-8 text-[hsl(var(--accent))] mb-3" />
          <h3 className="editorial-heading text-2xl mb-2">{marketplaceCount} lotti disponibili</h3>
          <p className="text-sm text-white/80 mb-4 flex-1">
            Esplora vini e food box pubblicati da altri rivenditori certificati. Margini interessanti, qty negoziabili.
          </p>
          <Link to="/b2b/lots/marketplace" data-testid="dash-marketplace-cta" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(var(--accent))] text-black text-sm font-semibold rounded-sm hover:bg-[hsl(var(--accent))]/90 transition-colors">
            Vai al marketplace <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testid, tone = "primary" }) {
  const tones = {
    primary: "border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))]",
    accent: "border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 text-[hsl(var(--accent))]",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <div data-testid={testid} className={`p-4 border rounded-sm ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function ToolCard({ to, icon: Icon, title, desc, testid, highlight }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group bg-card border rounded-sm p-5 hover:shadow-lg transition-all ${
        highlight ? "border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))]/30" : "border-border hover:border-[hsl(var(--accent))]"
      }`}
    >
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-3 transition-colors ${
        highlight ? "bg-[hsl(var(--accent))] text-black" : "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] group-hover:bg-[hsl(var(--accent))] group-hover:text-black"
      }`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-semibold text-foreground mb-0.5">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </Link>
  );
}

function StatusPill({ status }) {
  const cfg = {
    pending_approval: { label: "In attesa", c: "bg-amber-100 text-amber-800" },
    approved: { label: "Approvato", c: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Rifiutato", c: "bg-red-100 text-red-800" },
    sold: { label: "Venduto", c: "bg-blue-100 text-blue-800" },
    withdrawn: { label: "Ritirato", c: "bg-gray-100 text-gray-700" },
  }[status] || { label: status, c: "bg-gray-100 text-gray-700" };
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full ${cfg.c}`}>{cfg.label}</span>;
}
